import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Users } from 'lucide-react';
import { useLang } from '../context/LanguageContext';

/**
 * GoldenCircleTrustBadge
 * ---------------------------------------------------------
 * A compact "Golden Circle" trust strip for the homepage hero.
 * Contains three trust pillars:
 *   1. 33K+ Clients (gold seal)
 *   2. Media outlets (Zee · Aaj Tak · ABP · News18 · India TV)
 *   3. Verified Vedic Acharya (gold stamp)
 *
 * Design: wine-maroon glass card + hairline gold border, subtle
 * gold circular seals on each end, media pills in the middle.
 * ---------------------------------------------------------
 */

const MEDIA = [
  { name: 'Zee News' },
  { name: 'Aaj Tak' },
  { name: 'ABP News' },
  { name: 'News18' },
  { name: 'India TV' },
];

const GoldSeal = ({ icon: Icon, top, bottom }) => (
  <div className="relative flex-shrink-0" data-testid={`golden-seal-${bottom.toLowerCase().replace(/\s+/g, '-')}`}>
    {/* Outer gold ring with rotating subtle mandala */}
    <div className="relative w-[68px] h-[68px] sm:w-[76px] sm:h-[76px] rounded-full">
      <div className="absolute inset-0 rounded-full border-2 border-gold/60" />
      <div className="absolute inset-[3px] rounded-full border border-gold/25" />
      <div
        className="absolute inset-0 rounded-full mandala-spin"
        style={{
          background:
            'conic-gradient(from 0deg, transparent 0deg, rgba(255,215,0,0.35) 45deg, transparent 90deg, rgba(255,215,0,0.25) 180deg, transparent 240deg, rgba(255,215,0,0.35) 315deg, transparent 360deg)',
          animationDuration: '30s',
          WebkitMask:
            'radial-gradient(circle, transparent 62%, black 63%, black 100%)',
          mask:
            'radial-gradient(circle, transparent 62%, black 63%, black 100%)',
        }}
      />
      {/* Inner medallion */}
      <div className="absolute inset-[6px] rounded-full bg-gradient-to-br from-[#3d101e] via-[#2c0b14] to-[#1a0710] border border-gold/40 flex flex-col items-center justify-center shadow-[inset_0_0_12px_rgba(255,215,0,0.15)]">
        {Icon && <Icon className="w-3.5 h-3.5 text-gold/80 mb-0.5" strokeWidth={2.25} />}
        <span className="font-heading text-[15px] sm:text-[16px] leading-none font-semibold text-gold-gradient">
          {top}
        </span>
        <span className="text-[8px] sm:text-[9px] uppercase tracking-[0.14em] text-gold/70 mt-0.5 font-medium whitespace-nowrap">
          {bottom}
        </span>
      </div>
    </div>
  </div>
);

export default function GoldenCircleTrustBadge({ align = 'left' }) {
  const { lang } = useLang();
  const isHi = lang === 'hi';

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.05, duration: 0.65, ease: 'easeOut' }}
      className={`relative w-full max-w-2xl ${align === 'center' ? 'mx-auto' : ''}`}
      data-testid="golden-circle-trust-badge"
    >
      {/* Soft ambient glow behind the badge */}
      <div className="absolute -inset-3 rounded-[28px] bg-gradient-to-r from-gold/[0.04] via-gold/[0.08] to-gold/[0.04] blur-xl pointer-events-none" />

      {/* Card */}
      <div className="relative flex items-center gap-3 sm:gap-5 px-3 sm:px-5 py-3 sm:py-4 rounded-[22px] bg-gradient-to-br from-[#3d101e]/85 via-[#2c0b14]/90 to-[#1a0710]/85 backdrop-blur-xl border border-gold/25 shadow-[0_10px_40px_-15px_rgba(0,0,0,0.7),inset_0_1px_0_rgba(255,215,0,0.1)]">
        {/* Top hairline gold gradient */}
        <div className="absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent" />

        {/* Left seal — Clients */}
        <GoldSeal icon={Users} top="33K+" bottom={isHi ? 'यजमान' : 'Clients'} />

        {/* Middle — Media strip */}
        <div className="flex-1 min-w-0">
          <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.22em] text-gold/70 font-semibold mb-1.5 text-center">
            {isHi ? 'मीडिया में विशेष रूप से' : 'As Featured In'}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-1.5 sm:gap-2">
            {MEDIA.map((m) => (
              <span
                key={m.name}
                className="px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-white/[0.04] border border-gold/15 text-[10px] sm:text-[11px] font-medium text-text-muted whitespace-nowrap tracking-wide hover:border-gold/35 hover:text-gold/90 transition-colors duration-300"
                data-testid={`media-logo-${m.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {m.name}
              </span>
            ))}
          </div>
        </div>

        {/* Right seal — Verified Vedic Acharya */}
        <GoldSeal
          icon={ShieldCheck}
          top={isHi ? 'प्रमाणित' : 'Verified'}
          bottom={isHi ? 'आचार्य' : 'Vedic Acharya'}
        />
      </div>
    </motion.div>
  );
}
